/**
 * 实践审核驳回信：预置理由、localStorage 与拼信
 */

/** @deprecated 仅兼容旧 key，新数据按项目存于 getAuditSettingsStorageKey(projectId) */
export const LEGACY_STORAGE_KEY = 'selection_case_my_settings';

/**
 * 与 projectId 绑定，不同征集项目使用独立配置与自定义理由
 * @param {string|number|undefined|null} projectId
 * @returns {string}
 */
export function getAuditSettingsStorageKey(projectId) {
    const id = projectId == null || projectId === '' ? '0' : String(projectId);
    return `selection_case_audit_settings_p${id}`;
}

export const DEFAULT_SETTINGS = {
    dead_line: '',
    wework: '',
    /** 为 true 时可在可选理由中勾选「实践启动实施距今未满 N 年」，并显示年限配置 */
    impl_years_enabled: false,
    /** 实践「启动实施距今须满 N 年」的 N，同一项目一条；用于预置「实践启动实施距今未满…年」 */
    min_impl_years: '',
    my_reasons: [],
};

/** 与展示顺序一致；kind 为 page 时展示基础句、勾选后填页码、拼接时追加（第{页码}页），为 theme 时仅基础句、填写「投报至主题」后追加（请投报至主题{内容}），为 years 时年数取项目配置 min_impl_years */
export const PRESET_REASONS = [
    { id: 'ppt_template', kind: 'none', text: '未使用本项目专用PPT实践模板' },
    { id: 'content_incomplete', kind: 'page', text: '内容显示不全' },
    { id: 'hospital_name_logo', kind: 'page', text: '未规避申报单位名称、Logo等' },
    { id: 'hospital_leader', kind: 'page', text: '未规避申报单位院级领导照片或姓名等' },
    { id: 'map', kind: 'page', text: '中国地图不完整' },
    { id: 'patient_privacy', kind: 'page', text: '未保护患者隐私' },
    { id: 'duplicate', kind: 'none', text: '实践重复提交' },
    { id: 'theme', kind: 'theme', text: '实践内容与主题不符' },
    { id: 'impl_years', kind: 'years', text: '实践启动实施距今未满', suffix: '年' },
];

const PRESET_BY_ID = new Map(PRESET_REASONS.map(p => [p.id, p]));

/**
 * 按预置表顺序筛出已勾选的 id
 * @param {string[]} selectedIds
 * @returns {string[]}
 */
export function orderSelectedPresetIds(selectedIds) {
    if (!Array.isArray(selectedIds) || !selectedIds.length) {
        return [];
    }
    const set = new Set(selectedIds);
    return PRESET_REASONS.map(p => p.id).filter(id => set.has(id));
}

/**
 * 单格页码：在基础句后追加「（第{pageText}页）」
 * @param {string} line
 * @param {string} pageText
 * @returns {string}
 */
export function applyPagePlaceholders(line, pageText) {
    if (!pageText || !String(pageText).trim()) {
        return line;
    }
    const t = String(pageText).trim();
    return `${line}（第${t}页）`;
}

/**
 * 有主题时在后追加（请投报至主题{内容}）；无内容则返回基础句
 * @param {string} line
 * @param {string} themeText
 * @returns {string}
 */
export function applyThemePlaceholder(line, themeText) {
    const t = String(themeText ?? '').trim();
    if (!t) {
        return line;
    }
    return `${line}（请投报至主题${t}）`;
}

/**
 * @param {typeof DEFAULT_SETTINGS} settings
 * @returns {boolean}
 */
export function isDeadlineReady(settings) {
    return !!(settings && String(settings.dead_line ?? '').trim());
}

function normalizeSettingsPayload(p) {
    const min_impl_years = typeof p.min_impl_years === 'string' ? p.min_impl_years : '';
    let impl_years_enabled = p.impl_years_enabled;
    if (typeof impl_years_enabled !== 'boolean') {
        // 兼容旧数据：曾填写过年限则视为已开启该项
        impl_years_enabled = String(min_impl_years).trim() !== '';
    }
    return {
        dead_line: typeof p.dead_line === 'string' ? p.dead_line : '',
        wework: typeof p.wework === 'string' ? p.wework : '',
        impl_years_enabled,
        min_impl_years,
        my_reasons: Array.isArray(p.my_reasons) ? p.my_reasons.filter(x => typeof x === 'string') : [],
    };
}

/**
 * @param {string|number|undefined|null} projectId
 * @returns {typeof DEFAULT_SETTINGS}
 */
export function loadSettings(projectId) {
    const key = getAuditSettingsStorageKey(projectId);
    try {
        const raw = localStorage.getItem(key);
        if (!raw) {
            const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
            if (legacy) {
                try {
                    const migrated = normalizeSettingsPayload(JSON.parse(legacy));
                    localStorage.setItem(key, JSON.stringify(migrated));
                    localStorage.removeItem(LEGACY_STORAGE_KEY);
                    return { ...migrated, my_reasons: [...(migrated.my_reasons || [])] };
                } catch {
                    // 忽略单条坏数据
                }
            }
            return { ...DEFAULT_SETTINGS, my_reasons: [] };
        }
        const p = JSON.parse(raw);
        return normalizeSettingsPayload(p);
    } catch {
        return { ...DEFAULT_SETTINGS, my_reasons: [] };
    }
}

/**
 * @param {typeof DEFAULT_SETTINGS} settings
 * @param {string|number|undefined|null} projectId
 */
export function saveSettings(settings, projectId) {
    const key = getAuditSettingsStorageKey(projectId);
    const min_impl_years = typeof settings.min_impl_years === 'string' ? settings.min_impl_years : '';
    const impl_years_enabled = typeof settings.impl_years_enabled === 'boolean' ? settings.impl_years_enabled : String(min_impl_years).trim() !== '';
    const payload = {
        dead_line: typeof settings.dead_line === 'string' ? settings.dead_line : '',
        wework: typeof settings.wework === 'string' ? settings.wework : '',
        impl_years_enabled,
        min_impl_years,
        my_reasons: Array.isArray(settings.my_reasons) ? settings.my_reasons.filter(x => typeof x === 'string') : [],
    };
    try {
        localStorage.setItem(key, JSON.stringify(payload));
    } catch (e) {
        console.error('保存审核驳回配置失败', e);
    }
}

/**
 * 是否已勾选至少一条预置或自定义理由（驳回时）
 * @param {string[]|undefined} selectedPresetIds
 * @param {number[]|undefined} selectedMyCustom
 * @returns {boolean}
 */
export function hasRejectReasonSelection(selectedPresetIds, selectedMyCustom) {
    const n = Array.isArray(selectedPresetIds) ? selectedPresetIds.length : 0;
    const m = Array.isArray(selectedMyCustom) ? selectedMyCustom.length : 0;
    return n > 0 || m > 0;
}

/**
 * @param {object} params
 * @param {{ dead_line?: string, wework?: string, impl_years_enabled?: boolean, min_impl_years?: string }} params.settings
 * @param {string[]} params.selectedPresetIds
 * @param {Record<string, { page?: string, theme?: string }>} params.presetExtras
 * @param {string[]} [params.selectedCustomReasons]
 * @param {string[]} [params.aiReviewSegments] 已勾选的 AI 初审单条，顺序须与传参一致（一般为先 page_num、再 template、再 duplicate）
 * @param {string} [params.tempReason] 右栏临时驳回理由，按行拼入「待修改问题」末尾
 * @returns {string}
 */
export function buildRejectLetter({ settings, selectedPresetIds, presetExtras, selectedCustomReasons, aiReviewSegments, tempReason }) {
    const deadLine = String(settings?.dead_line ?? '').trim();
    const wework = String(settings?.wework ?? '');
    const ordered = orderSelectedPresetIds(selectedPresetIds);
    const lines = [];

    if (Array.isArray(aiReviewSegments)) {
        for (const seg of aiReviewSegments) {
            if (seg != null && String(seg).trim() !== '') {
                lines.push(String(seg).trim());
            }
        }
    }

    for (const id of ordered) {
        const p = PRESET_BY_ID.get(id);
        if (!p) {
            continue;
        }
        let line = p.text;
        if (p.kind === 'page') {
            const page = presetExtras?.[id]?.page?.trim() ?? '';
            if (page) {
                line = applyPagePlaceholders(line, page);
            }
        } else if (p.kind === 'theme') {
            const theme = presetExtras?.[id]?.theme?.trim() ?? '';
            if (theme) {
                line = applyThemePlaceholder(line, theme);
            }
        } else if (p.kind === 'years' && p.suffix) {
            if (!settings?.impl_years_enabled) {
                continue;
            }
            const y = String(settings?.min_impl_years ?? '').trim();
            line = p.text + y + p.suffix;
        }
        lines.push(line);
    }

    if (Array.isArray(selectedCustomReasons)) {
        for (const c of selectedCustomReasons) {
            if (c && String(c).trim()) {
                lines.push(String(c).trim());
            }
        }
    }

    const tempLines = String(tempReason ?? '')
        .split(/\r?\n/)
        .map(s => s.trim())
        .filter(Boolean);
    for (const line of tempLines) {
        lines.push(line);
    }

    const join = lines.length ? lines.join('；') : '无';

    return `尊敬的参与单位：您所提交的实践因不符合审核标准被驳回，请于${deadLine}前修改后重新提交。待修改问题：${join}。如有任何问题请联系组委会工作人员（企微号：${wework}）。`;
}

/**
 * 供提交前校验：已勾选年限理由但项目未配置年数（页码为选填，非空才拼入「（第X页）」）
 * @param {string[]} selectedPresetIds
 * @param {Record<string, { page?: string, theme?: string }>} [_presetExtras]
 * @param {{ impl_years_enabled?: boolean, min_impl_years?: string } | null | undefined} [settings] 与 loadSettings 同结构，用于 years 类预置
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validatePresetExtras(selectedPresetIds, _presetExtras, settings) {
    const ordered = orderSelectedPresetIds(selectedPresetIds);
    for (const id of ordered) {
        const p = PRESET_BY_ID.get(id);
        if (!p) {
            continue;
        }
        if (p.kind === 'years') {
            if (!settings?.impl_years_enabled) {
                return { ok: false, message: '请先在“驳回理由配置”中开启实践实施周期年限，或取消勾选该理由' };
            }
            const y = String(settings?.min_impl_years ?? '').trim();
            if (!y) {
                return { ok: false, message: '请在本项目“驳回理由配置”中填写实践实施起算年限' };
            }
        }
    }
    return { ok: true };
}

export { PRESET_BY_ID };
